import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { format } from "date-fns";
import { AlertCircle, Settings, User, Database, Globe, Shield, Activity, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getAuditLogs, getAuditStats } from "../api/axios";

// Type definitions matching backend schema
type AuditLog = {
  _id: string;
  userId?: string;
  username: string;
  action: string;
  resource: string;
  method: string;
  endpoint: string;
  requestParams?: any;
  requestBody?: any;
  responseStatus?: number;
  responseData?: any;
  ipAddress?: string;
  userAgent?: string;
  organization?: string;
  success: boolean;
  errorMessage?: string;
  duration?: number;
  createdAt: string | { $date: string };
  updatedAt: string | { $date: string };
  __v?: number;
};


export default function AuditLogs() {
  const { user: currentUser } = useAuth();
  const [daysFilter, setDaysFilter] = useState(7);
  const [limitFilter, setLimitFilter] = useState(500);
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedAction, setSelectedAction] = useState("all");
  const [selectedResource, setSelectedResource] = useState("all");
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  
  // Function to toggle date expansion
  const toggleDateExpansion = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };
  
  // Check if user has access to audit logs
  const hasAccess = currentUser?.role === "admin" || currentUser?.role === "superadmin";
  
  // Get audit logs from real API
  const { data: auditData, isLoading: logsLoading, error: logsError } = useQuery({
    queryKey: ["audit-logs", daysFilter, limitFilter],
    queryFn: () => getAuditLogs({ days: daysFilter, limit: limitFilter }),
    enabled: hasAccess,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1, // Reduce retries to avoid spamming
    refetchOnWindowFocus: false,
  });
  
  // Get audit statistics
  const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ["audit-stats", daysFilter],
    queryFn: () => getAuditStats({ days: daysFilter }),
    enabled: hasAccess,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Reduce retries to avoid spamming
    refetchOnWindowFocus: false,
  });
  
  // Note: Users for filtering are extracted from audit logs directly
  
  // Extract logs from response
  const auditLogs: AuditLog[] = auditData?.logs || [];
  
  // Access control check
  if (!hasAccess) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600">
            You need administrator privileges to view audit logs.
          </p>
        </div>
      </div>
    );
  }
  
  // Apply client-side filtering (since backend doesn't support advanced filtering yet)
  const filteredLogs = auditLogs.filter((log) => {
    if (selectedUser && selectedUser !== "all" && log.username !== selectedUser) return false;
    if (selectedAction && selectedAction !== "all" && !log.action.toLowerCase().includes(selectedAction.toLowerCase())) return false;
    if (selectedResource && selectedResource !== "all" && !log.resource.toLowerCase().includes(selectedResource.toLowerCase())) return false;
    return true;
  });
  
  // Get unique values for filters
  const uniqueUsers = [...new Set(auditLogs.map(log => log.username))].sort();
  const uniqueActions = [...new Set(auditLogs.map(log => log.action))].sort();
  const uniqueResources = [...new Set(auditLogs.map(log => log.resource))].sort();
  
  // Format timestamp
  const formatTimestamp = (timestamp: string | { $date: string }) => {
    try {
      // Handle MongoDB date format
      const dateString = typeof timestamp === 'string' ? timestamp : timestamp.$date;
      return format(new Date(dateString), "PPp");
    } catch (e) {
      return "Invalid date";
    }
  };
  
  // Get user-friendly action name
  const getActionName = (action: string) => {
    // Map technical action names to user-friendly names
    const actionMap: Record<string, string> = {
      // Device Metrics Actions
      'devices_metrics.getMultiDeviceOverview': 'viewed multi-device overview',
      'devices_metrics.getDashboard': 'viewed device dashboard',
      'devices_metrics.getSystemMetrics': 'viewed system metrics',
      'devices_metrics.getDeviceStatus': 'viewed device status',
      'devices_metrics.getInterfaceMetrics': 'viewed interface metrics',
      'devices_metrics.getDiskMetrics': 'viewed disk metrics',
      'devices_metrics.getTopInterfaces': 'viewed top interfaces',
      
      // Device Actions
      'devices.get': 'viewed device',
      'devices.list': 'viewed devices',
      'devices.create': 'created device',
      'devices.update': 'updated device',
      'devices.delete': 'deleted device',
      'devices.testConnection': 'tested device connection',
      'devices.runNetworkTest': 'ran network test',
      'devices.getMetrics': 'viewed device metrics',
      'devices.getAlerts': 'viewed device alerts',
      'devices.getScheduler': 'viewed device scheduler',
      
      // User Actions
      'users.login': 'logged in',
      'users.logout': 'logged out',
      'users.register': 'registered',
      'users.me': 'accessed user dashboard',
      'users.get': 'viewed user',
      'users.list': 'viewed users',
      'users.create': 'created user',
      'users.update': 'updated user',
      'users.delete': 'deleted user',
      'users.getProfile': 'viewed profile',
      'users.updateProfile': 'updated profile',
      'users.forgotPassword': 'requested password reset',
      'users.resetPassword': 'reset password',
      'users.getOrganization': 'viewed organization',
      
      // Alert Actions
      'alerts.get': 'viewed alert',
      'alerts.list': 'viewed alerts',
      'alerts.create': 'created alert',
      'alerts.update': 'updated alert',
      'alerts.delete': 'deleted alert',
      'alerts.acknowledge': 'acknowledged alert',
      'alerts.resolve': 'resolved alert',
      
      // Audit Actions
      'audits.log': 'logged audit entry',
      'audits.list': 'viewed audit logs',
      'audits.stats': 'viewed audit statistics',
      'CALL Audits.list': 'viewed audit logs',
      'CALL Audits.stats': 'viewed audit statistics',
      
      // Generic Actions
      'get': 'viewed',
      'list': 'viewed list of',
      'create': 'created',
      'update': 'updated',
      'delete': 'deleted',
      'login': 'logged in',
      'logout': 'logged out',
      'register': 'registered',
    };

    // Try exact match first
    if (actionMap[action]) {
      return actionMap[action];
    }

    // Try partial matches for similar patterns
    for (const [pattern, name] of Object.entries(actionMap)) {
      if (action.includes(pattern) || pattern.includes(action)) {
        return name;
      }
    }

    // Clean up technical action names
    if (action.includes('.')) {
      const parts = action.split('.');
      const method = parts[1] || '';
      
      // Convert camelCase method names to readable text
      const readableMethod = method
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .trim();
      
      if (readableMethod) {
        return readableMethod;
      }
    }

    // Handle CALL patterns (e.g., "CALL Audits.list")
    if (action.startsWith('CALL ')) {
      const callAction = action.replace('CALL ', '').toLowerCase();
      if (callAction.includes('audits.list') || callAction.includes('audit') && callAction.includes('list')) {
        return 'viewed audit logs';
      }
      if (callAction.includes('audits.stats') || callAction.includes('audit') && callAction.includes('stats')) {
        return 'viewed audit statistics';
      }
      if (callAction.includes('users.list') || callAction.includes('user') && callAction.includes('list')) {
        return 'viewed users';
      }
      if (callAction.includes('devices.list') || callAction.includes('device') && callAction.includes('list')) {
        return 'viewed devices';
      }
      if (callAction.includes('alerts.list') || callAction.includes('alert') && callAction.includes('list')) {
        return 'viewed alerts';
      }
      // Generic CALL handling
      const cleanCall = callAction.replace(/\./g, ' ').replace(/([A-Z])/g, ' $1').toLowerCase().trim();
      return cleanCall || 'performed action';
    }

    // Fallback: return action as-is but cleaned up
    return action.replace(/([A-Z])/g, ' $1').toLowerCase().trim() || 'performed action';
  };

  // Get function name from endpoint
  const getFunctionName = (endpoint: string, method: string, action: string) => {
    // Handle empty or undefined values
    if (!endpoint || endpoint.trim() === '') {
      // Use the same action mapping as getActionName for consistency
      const friendlyAction = getActionName(action);
      if (friendlyAction !== action) {
        return friendlyAction.charAt(0).toUpperCase() + friendlyAction.slice(1);
      }
      
      const methodMap: Record<string, string> = {
        'GET': 'View',
        'POST': 'Create',
        'PUT': 'Update',
        'PATCH': 'Modify',
        'DELETE': 'Delete'
      };
      const actionName = action ? action.charAt(0).toUpperCase() + action.slice(1) : 'Action';
      const methodName = methodMap[method] || method || 'Action';
      return `${methodName} ${actionName}`;
    }

    // Clean the endpoint - remove query parameters and trailing slashes
    const cleanEndpoint = endpoint.split('?')[0].replace(/\/$/, '');
    
    // Map API endpoints to user-friendly function names based on axios.ts
    const endpointMap: Record<string, string> = {
      // Authentication & User Management
      '/api/users/auth/login': 'User Login',
      '/api/users/auth/logout': 'User Logout',
      '/api/users/forgot-password': 'Forgot Password',
      '/api/users/reset-password': 'Reset Password',
      '/api/me': 'Get Profile',
      '/api/users': 'User Management',
      '/api/users/organization': 'Organization Management',

      // Device Management
      '/api/devices': 'Device Management',
      '/api/devices/sheduler': 'Device Scheduler',
      '/api/devices/test-connection': 'Test Device Connection',
      '/api/devices/network-test': 'Network Test',
      '/api/devices/metrics': 'Device Metrics',
      '/api/devices/alerts': 'Device Alerts',

      // Device Metrics (InfluxDB)
      '/api/devices_metrics/dashboard': 'Device Dashboard',
      '/api/devices_metrics/system': 'System Metrics',
      '/api/devices_metrics/status': 'Device Status',
      '/api/devices_metrics/interfaces': 'Interface Metrics',
      '/api/devices_metrics/disk': 'Disk Metrics',
      '/api/devices_metrics/topinterfaces': 'Top Interfaces',
      '/api/devices_metrics/overview': 'Multi-Device Overview',

      // Alert Management
      '/api/alerts': 'Alert Management',
      '/api/alerts/acknowledge': 'Acknowledge Alert',
      '/api/alerts/resolve': 'Resolve Alert',

      // Audit System
      '/api/audits/logs': 'View Audit Logs',
      '/api/audits/stats': 'Audit Statistics',

      // Frontend Pages/Routes (without /api prefix)
      '/dashboard': 'Dashboard Access',
      '/inventory': 'Inventory Management',
      '/performance': 'Performance Monitoring',
      '/reports': 'Report Generation',
      '/alerts': 'Alerts Page',
      '/audit-logs': 'Audit Logs Page',
      '/live-alerts': 'Live Alerts',
      '/notifications': 'Notifications',
      '/config-management': 'Configuration Management',
      '/ai-configuration': 'AI Configuration',
      '/sla-monitoring': 'SLA Monitoring',
      '/test-utilities': 'Test Utilities',
      '/network-topology': 'Network Topology',
      '/device-details': 'Device Details',
      '/add-device': 'Add Device',
      '/add-snmp': 'Add SNMP Device',
      '/add-ssh': 'Add SSH Device',
      '/help': 'Help & Support',
      '/profile': 'User Profile',
      '/login': 'Login Page',
      '/register': 'Registration Page',
      '/forget-password': 'Forgot Password Page',
      '/reset-password': 'Reset Password Page',
    };

    // Try to find exact match first
    if (endpointMap[cleanEndpoint]) {
      return endpointMap[cleanEndpoint];
    }

    // Try to find partial matches for dynamic endpoints with IDs
    for (const [pattern, name] of Object.entries(endpointMap)) {
      if (cleanEndpoint.startsWith(pattern) || cleanEndpoint.includes(pattern.replace('/api', ''))) {
        // Handle specific cases for dynamic routes
        if (pattern.includes('devices') && cleanEndpoint.match(/\/devices\/[^\/]+/)) {
          if (cleanEndpoint.includes('/test-connection')) return 'Test Device Connection';
          if (cleanEndpoint.includes('/network-test')) return 'Network Test';
          if (cleanEndpoint.includes('/metrics')) return 'Device Metrics';
          if (cleanEndpoint.includes('/alerts')) return 'Device Alerts';
          return 'Device Details';
        }
        if (pattern.includes('devices_metrics') && cleanEndpoint.match(/\/devices_metrics\/\w+\/[^\/]+/)) {
          if (cleanEndpoint.includes('/dashboard/')) return 'Device Dashboard';
          if (cleanEndpoint.includes('/system/')) return 'System Metrics';
          if (cleanEndpoint.includes('/status/')) return 'Device Status';
          if (cleanEndpoint.includes('/interfaces/')) return 'Interface Metrics';
          if (cleanEndpoint.includes('/disk/')) return 'Disk Metrics';
          if (cleanEndpoint.includes('/topinterfaces/')) return 'Top Interfaces';
        }
        if (pattern.includes('alerts') && cleanEndpoint.match(/\/alerts\/[^\/]+/)) {
          if (cleanEndpoint.includes('/acknowledge')) return 'Acknowledge Alert';
          if (cleanEndpoint.includes('/resolve')) return 'Resolve Alert';
          return 'Alert Management';
        }
        if (pattern.includes('users') && cleanEndpoint.match(/\/users\/[^\/]+/)) {
          return 'User Management';
        }
        if (pattern.includes('reset-password') && cleanEndpoint.includes('/reset-password/')) {
          return 'Reset Password';
        }
        return name;
      }
    }

    // Additional pattern matching for common REST endpoints
    if (cleanEndpoint.match(/\/api\/\w+$/)) {
      // Basic CRUD operations on collections
      const resource = cleanEndpoint.split('/').pop() || '';
      const resourceName = resource.charAt(0).toUpperCase() + resource.slice(1);
      const methodMap: Record<string, string> = {
        'GET': 'View',
        'POST': 'Create',
        'PUT': 'Update',
        'PATCH': 'Modify',
        'DELETE': 'Delete'
      };
      return `${methodMap[method] || method} ${resourceName}`;
    }

    // Fallback: create readable name from action and method
    const methodMap: Record<string, string> = {
      'GET': 'View',
      'POST': 'Create',
      'PUT': 'Update',
      'PATCH': 'Modify',
      'DELETE': 'Delete'
    };

    // Try to get user-friendly action name first
    const friendlyAction = getActionName(action);
    if (friendlyAction !== action && friendlyAction !== 'performed action') {
      return friendlyAction.charAt(0).toUpperCase() + friendlyAction.slice(1);
    }

    const actionName = action ? action.charAt(0).toUpperCase() + action.slice(1) : 'Resource';
    const methodName = methodMap[method] || method || 'Action';
    
    return `${methodName} ${actionName}`;
  };

  // Get icon for resource type
  const getResourceIcon = (resource: string) => {
    const resourceLower = resource.toLowerCase();
    if (resourceLower.includes("user")) return <User className="h-4 w-4 text-blue-500" />;
    if (resourceLower.includes("device")) return <Database className="h-4 w-4 text-green-500" />;
    if (resourceLower.includes("alert")) return <AlertCircle className="h-4 w-4 text-red-500" />;
    if (resourceLower.includes("config") || resourceLower.includes("setting")) return <Settings className="h-4 w-4 text-purple-500" />;
    if (resourceLower.includes("auth") || resourceLower.includes("login")) return <Shield className="h-4 w-4 text-orange-500" />;
    if (resourceLower.includes("audit")) return <Activity className="h-4 w-4 text-cyan-500" />;
    return <Globe className="h-4 w-4 text-gray-500" />;
  };
  
  // Get status badge
  const getStatusBadge = (log: AuditLog) => {
    if (log.success) {
      if (log.responseStatus && log.responseStatus >= 200 && log.responseStatus < 300) {
        return <Badge className="bg-green-100 text-green-800">Success</Badge>;
      }
      return <Badge className="bg-blue-100 text-blue-800">Completed</Badge>;
    } else {
      return <Badge variant="destructive">Failed</Badge>;
    }
  };
  
  // Get method badge color
  const getMethodBadge = (method: string) => {
    const colors = {
      GET: "bg-blue-100 text-blue-800",
      POST: "bg-green-100 text-green-800",
      PUT: "bg-yellow-100 text-yellow-800",
      DELETE: "bg-red-100 text-red-800",
      PATCH: "bg-purple-100 text-purple-800"
    };
    return (
      <Badge className={colors[method as keyof typeof colors] || "bg-gray-100 text-gray-800"}>
        {method}
      </Badge>
    );
  };
  
  // Group logs by date
  const groupLogsByDate = () => {
    const grouped: Record<string, { logs: AuditLog[], utcDate: string, localDate: string }> = {};
    
    filteredLogs.forEach((log) => {
      // Handle MongoDB date format
      const dateString = typeof log.createdAt === 'string' ? log.createdAt : log.createdAt.$date;
      const logDate = new Date(dateString);
      
      // Use UTC date for grouping to avoid timezone issues
      const year = logDate.getUTCFullYear();
      const month = logDate.getUTCMonth();
      const day = logDate.getUTCDate();
      const utcDate = new Date(year, month, day);
      const utcDateString = utcDate.toDateString();
      
      // Also get local date for display
      const localDateString = logDate.toDateString();
      
      if (!grouped[utcDateString]) {
        grouped[utcDateString] = {
          logs: [],
          utcDate: utcDateString,
          localDate: localDateString
        };
      }
      grouped[utcDateString].logs.push(log);
    });
    
    return Object.entries(grouped)
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
      .map(([, { logs, utcDate, localDate }]) => ({
        utcDate,
        localDate,
        logs: logs.sort((a, b) => {
          const dateA = typeof a.createdAt === 'string' ? a.createdAt : a.createdAt.$date;
          const dateB = typeof b.createdAt === 'string' ? b.createdAt : b.createdAt.$date;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        }),
      }));
  };
  
  const groupedLogs = groupLogsByDate();

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Audit Logs</h1>
          <p className="text-muted-foreground">
            Monitor and track system activities, user actions, and security events
          </p>
        </div>
      </div>

      {/* Service availability notice */}
      {auditLogs.length === 0 && !logsLoading && !logsError && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Info className="h-5 w-5 text-blue-500" />
            <span className="text-blue-700 font-medium">Audit Service Information</span>
          </div>
          <p className="text-blue-600 mt-1">
            The audit logging service appears to be unavailable or not configured. 
            This feature requires backend audit service to be properly set up.
          </p>
        </div>
      )}

      {/* Error handling */}
      {(logsError || statsError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-red-700 font-medium">Error loading audit data</span>
          </div>
          {logsError && (
            <p className="text-red-600 mt-1">
              Logs: {logsError.message || "Failed to fetch audit logs"}
            </p>
          )}
          {statsError && (
            <p className="text-red-600 mt-1">
              Stats: {statsError.message || "Failed to fetch audit statistics"}
            </p>
          )}
          <p className="text-red-500 mt-2 text-sm">
            This might be because the audit service is not running or you don't have the required permissions.
          </p>
        </div>
      )}

      <Tabs defaultValue="logs" className="space-y-6">
        <TabsList>
          <TabsTrigger value="logs">Audit Logs</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
              <CardDescription>
                Filter audit logs by time period, user, action, and resource
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Time Period Filter */}
                <div className="space-y-2">
                  <Label htmlFor="days-filter">Time Period</Label>
                  <Select value={daysFilter.toString()} onValueChange={(value) => setDaysFilter(parseInt(value))}>
                    <SelectTrigger id="days-filter">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Last 24 hours</SelectItem>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Limit Filter */}
                <div className="space-y-2">
                  <Label htmlFor="limit-filter">Max Results</Label>
                  <Select value={limitFilter.toString()} onValueChange={(value) => setLimitFilter(parseInt(value))}>
                    <SelectTrigger id="limit-filter">
                      <SelectValue placeholder="Select limit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100">100 logs</SelectItem>
                      <SelectItem value="500">500 logs</SelectItem>
                      <SelectItem value="1000">1000 logs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* User Filter */}
                <div className="space-y-2">
                  <Label htmlFor="user-filter">User</Label>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger id="user-filter">
                      <SelectValue placeholder="All users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All users</SelectItem>
                      {uniqueUsers.map((username) => (
                        <SelectItem key={username} value={username}>
                          {username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action Filter */}
                <div className="space-y-2">
                  <Label htmlFor="action-filter">Action</Label>
                  <Select value={selectedAction} onValueChange={setSelectedAction}>
                    <SelectTrigger id="action-filter">
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      {uniqueActions.map((action) => (
                        <SelectItem key={action} value={action}>
                          {getActionName(action)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Resource Filter */}
                <div className="space-y-2">
                  <Label htmlFor="resource-filter">Resource</Label>
                  <Select value={selectedResource} onValueChange={setSelectedResource}>
                    <SelectTrigger id="resource-filter">
                      <SelectValue placeholder="All resources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All resources</SelectItem>
                      {uniqueResources.map((resource) => (
                        <SelectItem key={resource} value={resource}>
                          {resource}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Clear Filters */}
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedUser("all");
                    setSelectedAction("all");
                    setSelectedResource("all");
                    setLimitFilter(500);
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Logs List */}
          <div className="space-y-4">
            {logsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <span className="ml-2">Loading audit logs...</span>
              </div>
            ) : filteredLogs.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
                    <p className="text-gray-600">
                      {auditLogs.length === 0
                        ? "No audit logs available for the selected time period."
                        : "No logs match your current filter criteria."}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Data Information */}
                {auditData && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-4">
                        <span>Showing {auditData.count} of {auditData.total} total logs</span>
                        <span>•</span>
                        <span>Period: {auditData.days} days</span>
                        <span>•</span>
                        <span>Limit: {auditData.limit}</span>
                      </div>
                      {auditData.hasMore && (
                        <Badge variant="outline" className="text-blue-600 border-blue-600">
                          More data available
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Show data availability notice */}
                {groupedLogs.length === 1 && daysFilter > 1 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <Info className="h-5 w-5 text-blue-500" />
                      <span className="text-blue-700 font-medium">Limited Data Available</span>
                    </div>
                    <p className="text-blue-600 mt-1">
                      Audit logs are only available for {groupedLogs.length} day{groupedLogs.length !== 1 ? 's' : ''} 
                      out of the requested {daysFilter} days. More data will appear as you continue using the system.
                    </p>
                  </div>
                )}
                
                {/* Logs List with Fixed Header Design */}
                <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                  <div className="max-h-[600px] overflow-y-auto">
                    {groupedLogs.map(({ utcDate, localDate, logs }, _index) => {
                      const isExpanded = expandedDates.has(utcDate);
                      const visibleLogs = isExpanded ? logs : logs.slice(0, 5);
                      const hasMore = logs.length > 5;
                      
                      return (
                        <div key={utcDate} className="border-b border-gray-100 last:border-b-0">
                          {/* Date Header - Normal Static Header */}
                          <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {utcDate}
                                  {utcDate !== localDate && (
                                    <span className="text-sm font-normal text-gray-500 ml-2">
                                      (Local: {localDate})
                                    </span>
                                  )}
                                </h3>
                                <p className="text-sm text-gray-600">
                                  {logs.length} events
                                  {hasMore && !isExpanded && (
                                    <span className="ml-1 text-blue-600">• Showing first 5</span>
                                  )}
                                </p>
                              </div>
                              {hasMore && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => toggleDateExpansion(utcDate)}
                                  className="text-xs"
                                >
                                  {isExpanded ? 'Show Less' : `Show All (${logs.length})`}
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Logs List */}
                          <div className="divide-y divide-gray-100">
                            {visibleLogs.map((log, _logIndex) => (
                              <div
                                key={log._id}
                                className="p-4 hover:bg-gray-50 transition-colors duration-150"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                                    {getResourceIcon(log.resource)}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <span className="font-medium text-gray-900">
                                          {log.username}
                                        </span>
                                        <span className="text-gray-500">
                                          {getActionName(log.action)}
                                        </span>
                                        <span className="text-gray-500">on</span>
                                        <span className="font-medium text-gray-900">
                                          {log.resource}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                                        <span>{formatTimestamp(log.createdAt)}</span>
                                        {getMethodBadge(log.method)}
                                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                          {getFunctionName(log.endpoint || '', log.method || 'GET', log.action || 'unknown')}
                                        </span>
                                        {log.duration && (
                                          <span className="text-xs">
                                            {log.duration}ms
                                          </span>
                                        )}
                                      </div>
                                      {log.errorMessage && (
                                        <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                                          Error: {log.errorMessage}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2 ml-4">
                                    {getStatusBadge(log)}
                                    {log.responseStatus && (
                                      <Badge 
                                        variant="outline" 
                                        className={`${
                                          log.responseStatus >= 200 && log.responseStatus < 300
                                            ? "border-green-200 text-green-700"
                                            : log.responseStatus >= 400
                                            ? "border-red-200 text-red-700"
                                            : "border-gray-200 text-gray-700"
                                        }`}
                                      >
                                        {log.responseStatus}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            
                            {hasMore && !isExpanded && (
                              <div className="p-4 text-center bg-gray-50 border-t border-gray-100">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleDateExpansion(utcDate)}
                                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                >
                                  Show {logs.length - 5} more events from {utcDate}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          {statsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <span className="ml-2">Loading analytics...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Total Logs */}
              <Card>
                <CardHeader>
                  <CardTitle>Total Events</CardTitle>
                  <CardDescription>Last {daysFilter} days</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{auditLogs.length}</div>
                  <p className="text-sm text-muted-foreground">
                    {Math.round(auditLogs.length / daysFilter)} per day average
                  </p>
                </CardContent>
              </Card>

              {/* Success Rate */}
              <Card>
                <CardHeader>
                  <CardTitle>Success Rate</CardTitle>
                  <CardDescription>Successful operations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {auditLogs.length > 0
                      ? Math.round((auditLogs.filter(log => log.success).length / auditLogs.length) * 100)
                      : 0}%
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {auditLogs.filter(log => log.success).length} of {auditLogs.length} events
                  </p>
                </CardContent>
              </Card>

              {/* Active Users */}
              <Card>
                <CardHeader>
                  <CardTitle>Active Users</CardTitle>
                  <CardDescription>Unique users with activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{uniqueUsers.length}</div>
                  <p className="text-sm text-muted-foreground">
                    In the last {daysFilter} days
                  </p>
                </CardContent>
              </Card>

              {/* Top Actions */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Top Actions</CardTitle>
                  <CardDescription>Most frequent actions</CardDescription>
                </CardHeader>
                <CardContent>
                  {statsData?.actions && statsData.actions.length > 0 ? (
                    <div className="space-y-2">
                      {statsData.actions.slice(0, 5).map((action: any) => (
                        <div key={action._id} className="flex items-center justify-between">
                          <span className="font-medium">{getActionName(action._id)}</span>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline">{action.count} times</Badge>
                            <span className="text-sm text-green-600">
                              {Math.round((action.success / action.count) * 100)}% success
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No action statistics available</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Errors */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Errors</CardTitle>
                  <CardDescription>Failed operations</CardDescription>
                </CardHeader>
                <CardContent>
                  {statsData?.errors && statsData.errors.length > 0 ? (
                    <div className="space-y-2">
                      {statsData.errors.slice(0, 3).map((error: any) => (
                        <div key={error._id} className="flex items-center justify-between">
                          <span className="font-medium text-red-600">{error._id}</span>
                          <Badge variant="destructive">{error.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-green-600">No recent errors</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}